import type { DocBlock } from "@/content/docs/types";

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const FAQ_HEADING = "Common questions";

/**
 * The heading text a block contributes to the page outline, or null.
 *
 * The `limits` callout and the FAQ block are headings too. They carry the most
 * quotable content on a capability page — what it does not do, and the
 * questions people actually type — and both sit after the last `h2`. Without
 * their own heading they get no anchor and no contents entry, and an extractor
 * attributes them to whatever unrelated section preceded them.
 */
export function headingTextFor(block: DocBlock): { level: 2 | 3; text: string } | null {
  if (block.type === "h2") return { level: 2, text: block.text };
  if (block.type === "h3") return { level: 3, text: block.text };
  if (block.type === "callout" && block.variant === "limits") {
    return { level: 2, text: block.title ?? "What this does not do" };
  }
  if (block.type === "faq") return { level: 2, text: block.title ?? FAQ_HEADING };
  return null;
}

/**
 * Stable anchor id per heading block, keyed by block index.
 *
 * Computed once and shared by the renderer and the table of contents so the
 * two can never disagree. Repeated heading text gets a numeric suffix — two
 * "What this does not do" headings on one page would otherwise collide and
 * send every anchor to the first.
 */
export function headingIds(blocks: readonly DocBlock[]): Map<number, string> {
  const ids = new Map<number, string>();
  const seen = new Map<string, number>();

  blocks.forEach((block, index) => {
    const heading = headingTextFor(block);
    if (!heading) return;
    const base = slugify(heading.text) || "section";
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    ids.set(index, count === 0 ? base : `${base}-${count + 1}`);
  });

  return ids;
}

export type OutlineEntry = { id: string; level: 2 | 3; text: string };

/** The page outline in document order, for the contents rail. */
export function docOutline(blocks: readonly DocBlock[]): OutlineEntry[] {
  const ids = headingIds(blocks);
  const entries: OutlineEntry[] = [];
  blocks.forEach((block, index) => {
    const heading = headingTextFor(block);
    const id = ids.get(index);
    if (heading && id) entries.push({ id, level: heading.level, text: heading.text });
  });
  return entries;
}
